import React from 'react';
import {
  View,
  StyleSheet,
  Text,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'react-native-element-dropdown';
import { getFontSize, getResponsiveValue } from '../utils';
import CloseIcon from './Icons/CloseIcon';
import CategoryIcon from './Icons/CategoryIcon';

const SearchableCategoryDropdown = ({
  value,
  onSelect,
  placeholder = 'Select a category',
  disabled = false,
  style,
  colorScheme = 'default', // 'default' or 'landing'
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  // Predefined categories with "Others" option
  const predefinedCategories = [
    {
      id: 'healthcare',
      name: 'Healthcare',
      icon: '🏥',
      description: 'Hospitals, clinics, pharmacies',
    },
    {
      id: 'banking',
      name: 'Banking',
      icon: '🏦',
      description: 'Banks, ATMs, financial services',
    },
    {
      id: 'education',
      name: 'Education',
      icon: '🎓',
      description: 'Schools, universities, training centers',
    },
    {
      id: 'retail',
      name: 'Retail',
      icon: '🛍️',
      description: 'Stores, malls, shopping centers',
    },
    {
      id: 'events',
      name: 'Events',
      icon: '🎪',
      description: 'Concerts, exhibitions, conferences',
    },
    {
      id: 'government',
      name: 'Government Office',
      icon: '🏛️',
      description: 'Offices, services, permits',
    },
    {
      id: 'restaurant',
      name: 'Restaurant & Food',
      icon: '🍽️',
      description: 'Restaurants, cafes, food services',
    },
    {
      id: 'automotive',
      name: 'Automotive',
      icon: '🚗',
      description: 'Car dealerships, service centers',
    },
    {
      id: 'technology',
      name: 'Technology',
      icon: '💻',
      description: 'IT services, software companies',
    },
    {
      id: 'real_estate',
      name: 'Real Estate',
      icon: '🏠',
      description: 'Property agencies, developers',
    },
    {
      id: 'salon',
      name: 'Salon',
      icon: '💇‍♀️',
      description: 'Hair salons, beauty parlors, spas',
    },
    {
      id: 'others',
      name: 'Others',
      icon: '🔧',
      description: 'Other business categories',
    },
  ];

  const handleSelect = (item) => {
    onSelect(item);
  };

  const handleClear = () => {
    onSelect(null);
  };

  const renderItem = (item) => {
    return (
      <View style={styles.itemContainer}>
        <Text style={styles.itemIcon}>{item.icon}</Text>
        <View style={styles.itemInfo}>
          <Text
            style={[
              styles.itemName,
              {
                color:
                  colorScheme === 'landing'
                    ? '#FFFFFF'
                    : theme.colors.onSurface,
              },
            ]}
          >
            {item.name}
          </Text>
          <Text
            style={[
              styles.itemDescription,
              {
                color:
                  colorScheme === 'landing'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : theme.colors.onSurfaceVariant,
              },
            ]}
          >
            {item.description}
          </Text>
        </View>
      </View>
    );
  };

  const styles = StyleSheet.create({
    container: {
      ...style,
    },
    dropdownContainer: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    categoryIconContainer: {
      marginRight: getResponsiveValue(10, 8, 6),
      alignItems: 'center',
      justifyContent: 'center',
    },
    dropdown: {
      flex: 1,
      minHeight: getResponsiveValue(48, 44, 40),
      borderWidth: 1,
      borderRadius: 8,
      paddingHorizontal: getResponsiveValue(16, 12, 8),
      paddingVertical: getResponsiveValue(14, 10, 8),
      backgroundColor:
        colorScheme === 'landing'
          ? 'rgba(255, 255, 255, 0.1)'
          : colorScheme === 'business'
            ? theme.colors.onBackground
            : theme.colors.surface,
      borderColor:
        colorScheme === 'landing'
          ? 'rgba(255, 255, 255, 0.2)'
          : colorScheme === 'business'
            ? theme.colors.borderColor
            : theme.colors.outline,
      ...(colorScheme === 'landing' && Platform.OS === 'web'
        ? {
            backdropFilter: 'blur(10px)',
          }
        : {}),
    },
    clearButton: {
      marginLeft: 8,
      padding: 6,
      borderRadius: 16,
      backgroundColor:
        colorScheme === 'landing'
          ? 'rgba(255, 255, 255, 0.12)'
          : colorScheme === 'business'
            ? 'rgba(0, 0, 0, 0.06)'
            : 'rgba(0, 0, 0, 0.06)',
      borderWidth: 1,
      borderColor:
        colorScheme === 'landing'
          ? 'rgba(255, 255, 255, 0.2)'
          : theme.colors.outline,
      justifyContent: 'center',
      alignItems: 'center',
    },
    itemContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
      paddingHorizontal: 16,
    },
    itemIcon: {
      fontSize: getFontSize(20, 18, 16),
      marginRight: 12,
      width: 24,
      textAlign: 'center',
    },
    itemInfo: {
      flex: 1,
    },
    itemName: {
      fontSize: getFontSize(16, 14, 12),
      fontFamily: 'Poppins',
      fontWeight: '500',
      marginBottom: 2,
    },
    itemDescription: {
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
    },
  });

  return (
    <View style={styles.container}>
      <View style={styles.dropdownContainer}>
        <Dropdown
          style={styles.dropdown}
          placeholderStyle={{
            color:
              colorScheme === 'landing'
                ? 'rgba(255, 255, 255, 0.5)'
                : colorScheme === 'business'
                  ? theme.colors.placeHolderText
                  : theme.colors.onSurfaceVariant,
            fontSize: getFontSize(16, 14, 12),
            fontFamily: 'Poppins',
            ...(Platform.OS === 'web'
              ? {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }
              : {}),
          }}
          selectedTextStyle={{
            color:
              colorScheme === 'landing'
                ? '#FFFFFF'
                : colorScheme === 'business'
                  ? theme.colors.inputTxtColor
                  : theme.colors.onSurface,
            fontSize: getFontSize(16, 14, 12),
            fontFamily: 'Poppins',
            ...(Platform.OS === 'web'
              ? {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }
              : {}),
          }}
          inputSearchStyle={{
            color:
              colorScheme === 'landing'
                ? '#FFFFFF'
                : colorScheme === 'business'
                  ? theme.colors.inputTxtColor
                  : theme.colors.onSurface,
            fontSize: getFontSize(14, 12, 10),
            fontFamily: 'Poppins',
          }}
          iconStyle={{
            width: 20,
            height: 20,
          }}
          data={predefinedCategories}
          search
          maxHeight={300}
          labelField="name"
          valueField="id"
          placeholder={placeholder}
          searchPlaceholder="Search categories..."
          value={value}
          onChange={handleSelect}
          renderItem={renderItem}
          dropdownPosition="bottom"
          containerStyle={{
            backgroundColor:
              colorScheme === 'landing'
                ? '#04264E'
                : colorScheme === 'business'
                  ? theme.colors.surface
                  : theme.colors.surface,
            borderRadius: 8,
            borderWidth: 1,
            borderColor:
              colorScheme === 'landing'
                ? 'rgba(255, 255, 255, 0.1)'
                : colorScheme === 'business'
                  ? theme.colors.borderColor
                  : theme.colors.outline,
          }}
          itemContainerStyle={{
            borderBottomWidth: 1,
            borderBottomColor:
              colorScheme === 'landing'
                ? 'rgba(255, 255, 255, 0.1)'
                : colorScheme === 'business'
                  ? theme.colors.borderColor
                  : theme.colors.outline,
          }}
          activeColor={
            colorScheme === 'landing'
              ? 'rgba(76, 175, 80, 0.2)'
              : colorScheme === 'business'
                ? theme.colors.primaryContainer
                : theme.colors.primaryContainer
          }
          searchQuery={(keyword, labelValue) => {
            if (keyword) {
              const item = predefinedCategories.find(
                (cat) => cat.id === labelValue
              );
              return (
                item &&
                (item.name.toLowerCase().includes(keyword.toLowerCase()) ||
                  item.description
                    .toLowerCase()
                    .includes(keyword.toLowerCase()))
              );
            }
            return true;
          }}
          renderLeftIcon={() => (
            <View style={styles.categoryIconContainer}>
              <CategoryIcon
                width={getResponsiveValue(18, 16, 14)}
                height={getResponsiveValue(18, 16, 14)}
                color={
                  colorScheme === 'landing'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : theme.colors.onSurface + 'B3'
                }
              />
            </View>
          )}
        />
        {value && (
          <TouchableOpacity style={styles.clearButton} onPress={handleClear}>
            <CloseIcon
              width={16}
              height={16}
              color={
                colorScheme === 'landing'
                  ? 'rgba(255,255,255,0.85)'
                  : theme.colors.onSurface
              }
            />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

export default SearchableCategoryDropdown;
